import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Stack,
  TextField,
  styled,
  Drawer,
  DialogActions,
  DialogContent,
  IconButton,
  Typography,
  List,
  MenuItem,
  InputAdornment
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import CheckIcon from "@mui/icons-material/Check";
import { C_SECONDARY_CONTAINER } from "~/common/components/icon/Colors";
import { useCountryOptionsWithUrl } from "~/Cart/pages/CartPage/B2CCart/components/B2CRequestPage/utils";
import { Button } from "~/common/components/Button/Button";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { STICKY_TO_BOTTOM } from "~/common/components/icon/Elevation";

interface DrawerProps {
  selectedCountry: string;
  closeDrawer: () => void;
  handleApply: (value: any) => void
}

export const MobileCountrySelector = ({ selectedCountry, closeDrawer, handleApply }: DrawerProps) => {
  const { t } = useTranslation();
  const countryOptions = useCountryOptionsWithUrl();

  const [inputValue, setInputValue] = useState("");
  const [filteredOptions, setFilteredOptions] = useState(countryOptions);

  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const selected = countryOptions.find((c) => c.value === selectedCountry);
    if (typeof selected?.label === "string" && selected?.label) {
      setInputValue(selected.label);
    }
  }, [selectedCountry, countryOptions]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      inputRef.current?.focus();
    }, 100);
    return () => clearTimeout(timeout);
  }, []);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setInputValue(value);

    const filtered = countryOptions.filter((option) =>
      typeof option.label === "string" &&
      option.label.toLowerCase().includes(value.toLowerCase())
    );
    setFilteredOptions(filtered);
  };

  const handleOptionClick = (option: typeof countryOptions[number]) => {
    setInputValue(option.stringLabel || option.label as string);
    setFilteredOptions(countryOptions);
  };

  const handleClear = () => {
    setInputValue("");
    setFilteredOptions(countryOptions);
  };

  const renderCountryList = () => {
    if (filteredOptions.length === 0) {
      return (
        <MenuItem key="no-results">
          {t("forkPage.country.search.noresults")}
        </MenuItem>
      );
    }

    return filteredOptions.map((option) => (
      <StyledMenuItem
        key={option.value}
        onClick={() => handleOptionClick(option)}
        selected={inputValue === option.label}
      >
        {option.label}
        {inputValue === option.label && <CheckIcon />}
      </StyledMenuItem>
    ));
  };

  const content = (
    <>
      <TextField
        label={t("generic.button.search")}
        value={inputValue}
        onChange={handleInputChange}
        inputRef={inputRef}
        fullWidth
        slotProps={{
          input: {
            style: {
              overflowX: "hidden",
              whiteSpace: "nowrap",
              textOverflow: "ellipsis",
            },
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: inputValue && (
              <InputAdornment position="end">
                <ClearIcon onClick={handleClear} />
              </InputAdornment>
            ),
          },
        }}
      />
      <StyledList>{renderCountryList()}</StyledList>
    </>
  )

  const footer = (
    <Stack direction={"row"} gap={1} className={"w-100"}>
      <Button
        variant={"text"}
        fullWidth={true}
        data-test-id={"fork-page-btn-cancel"}
        onClick={closeDrawer}
      >
        {t("cancel")}
      </Button>
      <Button
        size={"medium"}
        fullWidth={true}
        data-test-id={"fork-page-btn-apply"}
        disabled={inputValue.length === 0}
        onClick={() => handleApply(inputValue)}
      >
        {t("forkPage.apply.btn")}
      </Button>
    </Stack>
  );

  return (
    <Drawer
      anchor="bottom"
      open={true}
      onClose={closeDrawer}
      slotProps={{ paper: { className: "scha__add-to-cart-popup" } }}
    >
      <DrawerHeader onClose={closeDrawer}>
        <Typography variant="titleLarge">{t("forkPage.country.input")}</Typography>
      </DrawerHeader>
      <DrawerContent>{content}</DrawerContent>
      <DrawerActions>{footer}</DrawerActions>
    </Drawer>
  )
}

export const DrawerHeader = styled((props: JSX.IntrinsicElements["div"] & { onClose?: () => void }) => {
  const { onClose, children, ...rest } = props;
  return (
    <div {...rest}>
      <div>{children}</div>
      <IconButton variant="contained" color={"secondary"} size={"tiny"} onClick={onClose}>
        <CloseRoundedIcon />
      </IconButton>
    </div>
  );
})({
  padding: "16px 16px 8px",
  display: "grid",
  gridTemplateColumns: "1fr auto",
  alignItems: "center",
  ["& .MuiIconButton-sizeMedium"]: {
    padding: 0,
  },
});

export const DrawerContent = styled(DialogContent)({
  padding: "16px",
  height: "100vh",
  [`.MuiMenuItem-root:active`]: {
    background: `${C_SECONDARY_CONTAINER}`,
  },
  [`.MuiMenuItem-root:hover`]: {
    background: "unset",
  },
});

export const DrawerActions = styled(DialogActions)({
  padding: "8px 16px",
  boxShadow: STICKY_TO_BOTTOM,
});

export const StyledList = styled(List)({
  overflowY: "auto",
  overflowX: "hidden",
  height: "calc(100vh - 196px)"
});

export const StyledMenuItem = styled(MenuItem)({
  display: "flex",
  justifyContent: "space-between",
  height: "56px"
});
